import React, { createContext, useContext, useEffect, useState } from 'react';

import { QALITY_TEST_ISSUE_TYPE } from '@qality/common-config/src/constants';
import {
  Filter,
  Folder,
  Issue,
  Project,
  RepositoryIssue,
  TestCaseInFolder,
} from '@qality/common-model';
import { IssuesAndTotal } from '@qality/common-model/src/JQLSearch';
import { IssueTestType, TestType } from '@qality/common-model/src/TestType';
import {
  FlagService,
  FolderService,
  StorageOfUniqueServices,
  TestCasesService,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';
import DialogUtils from '@qality/common-utils/src/DialogUtils';

import { useTestCaseRepositoryFiltersContext } from '../TestCaseRepositoryFilters';
import { useTestCaseRepositoryFoldersContext } from '../TestCaseRepositoryFoldersProvider';
import { useTestCaseRepositoryIssueTypesContext } from '../TestCaseRepositoryIssueTypesProvider';
import { useTestCaseRepositorySelectionContext } from '../TestCaseRepositorySelectionProvider';
import { Key } from '../TestCaseRepositorySelectionProvider/useIssuesSelection';
import {
  filterOutIssues,
  getAreAllNewIssuesInFolderMatchingFilterCriteria,
  getCreateTestCaseInAllTestCasesWithFiltersFlagMessage,
  getCreateTestCaseInFolderFlagMessage,
  getCreateTestCaseInFolderWithFiltersFlagMessage,
  getFolderIssues,
  getSelectedFolderIssues,
} from './TestCaseRepositoryIssuesProvider.util';
import useTestCaseSearchPaginated from './useTestCaseSearchPaginated';

type Empty =
  | 'all-test-cases'
  | 'folder-with-filters'
  | 'folder-without-filter'
  | 'none';

export type TestCaseRepositoryIssuesContextValue = {
  issues: Array<RepositoryIssue>;
  loading: Loading;
  empty: Empty;
  hasMoreIssues: boolean;
  searchIssues: () => Promise<void>;
  searchMoreIssues: () => Promise<void>;
  totalIssues: number;
  createTestCases: (project: Project) => void;
  deleteTestCasesFromFolder: (
    testCasesInFolder: Array<TestCaseInFolder>
  ) => void;
  initFolder: () => Promise<void>;
  initSubfolder: () => Promise<void>;
  selectionKey: Key;
};

export const initialContextValue: TestCaseRepositoryIssuesContextValue = {
  issues: [],
  loading: 'none',
  empty: 'none',
  hasMoreIssues: false,
  searchIssues: () => Promise.resolve(),
  searchMoreIssues: () => Promise.resolve(),
  totalIssues: 0,
  createTestCases: () => undefined,
  deleteTestCasesFromFolder: () => undefined,
  initFolder: () => undefined,
  initSubfolder: () => undefined,
  selectionKey: 'all-test-cases',
};

export const TestCaseRepositoryIssuesContext =
  createContext<TestCaseRepositoryIssuesContextValue>(initialContextValue);

export const useTestCaseRepositoryIssuesContext =
  (): TestCaseRepositoryIssuesContextValue => {
    return useContext(TestCaseRepositoryIssuesContext);
  };

const NUMBER_OF_TEST_CASES_PER_PAGE_LIMIT = 50;

export type Loading =
  | 'none'
  | 'init'
  | 'search'
  | 'search-more'
  | 'create-test-case';

type Props = {
  children: React.ReactNode;
  folder: Folder;
  shouldFetchOnRender?: boolean;
};

export default function TestCaseRepositoryIssuesProvider({
  children,
  folder,
  shouldFetchOnRender = true,
}: Props): JSX.Element {
  const [loading, setLoading] = useState<Loading>('none');

  const [issues, setIssues] = useState<Array<RepositoryIssue>>([]);

  const { clearSelectedIssues, clearAllSelectedIssues, getKey } =
    useTestCaseRepositorySelectionContext();

  const [allIssuesInFolder, setAllIssuesInFolder] = useState<
    Array<RepositoryIssue>
  >([]);

  const [totalIssues, setTotalIssues] = useState(0);

  const [hasMoreIssues, setHasMoreIssues] = useState(false);

  const selectionKey = folder
    ? getKey('folder', folder.id)
    : getKey('all-test-cases');

  const IS_ADVANCED = StorageOfUniqueServices.get('LicenseService').isAdvanced;

  const {
    resetFiltersToDefaults,
    applyFilters,
    getProjectsForQuery,
    getStatusesForQuery,
    getTestTypeForQuery,
    areFiltersEmpty,
    textSearchFilter,
    filterCount,
  } = useTestCaseRepositoryFiltersContext();

  const { currentView, addToFolder } = useTestCaseRepositoryFoldersContext();
  const {
    getNextTestCasesPage: getNextTestCasesSearchPaginated,
    resetPageableTokens,
    pageNumberRef,
  } = useTestCaseSearchPaginated();

  const { testCasesTestTypes, updateTestCasesTestTypes } =
    useTestCaseRepositoryIssueTypesContext();

  useEffect(() => {
    if (shouldFetchOnRender) {
      folder?.id ? initFolder() : initAllTestCases();
    }
  }, [folder?.id]);

  useEffect(() => {
    if (currentView === 'folder' && IS_ADVANCED) {
      const foundTestCasesTypes = testCasesTestTypes.map(
        (testType) => testType.testCaseId
      );

      const issuesWithMissingTestType = issues.filter(
        (issue) => !foundTestCasesTypes.includes(Number(issue.id))
      );

      if (issuesWithMissingTestType.length > 0) {
        addMissingTestTypes(issuesWithMissingTestType);
      }
    }
  }, [issues.length]);

  useEffect(() => {
    if (filterCount > 0) {
      searchIssues();
    }
  }, [filterCount]);

  useEffect(() => {
    if (folder?.id) {
      updateTestCasesInFolder(folder?.testCases);
    }
  }, [folder?.testCases.length]);

  const initFolder = async () => {
    setLoading('init');
    pageNumberRef.current = 0;
    resetPageableTokens();

    resetFiltersToDefaults();

    const folderIssues = await getFolderIssues(folder);
    setAllIssuesInFolder(folderIssues);
    setIssues(folderIssues);
    setTotalIssues(folder.testCases.length);

    setHasMoreIssues(false);

    clearSelectedIssues(getKey('all-test-cases'));

    setLoading('none');
    if (IS_ADVANCED) {
      addMissingTestTypes(folderIssues);
    }
  };

  const initSubfolder = async () => {
    setLoading('init');
    pageNumberRef.current = 0;
    resetPageableTokens();

    const folderIssues = await getFolderIssues(folder);
    setAllIssuesInFolder(folderIssues);

    filterIssuesInFolder(folderIssues);

    setHasMoreIssues(false);
    setLoading('none');
    if (IS_ADVANCED) {
      addMissingTestTypes(folderIssues);
    }
  };

  const addMissingTestTypes = async (
    testCases: RepositoryIssue[]
  ): Promise<void> => {
    const foundTestCasesTestTypes = await searchForTestCasesTestTypes(
      testCases
    );
    updateTestCasesTestTypes(foundTestCasesTestTypes);
  };

  const initAllTestCases = async () => {
    setLoading('init');

    clearAllSelectedIssues();
    await getFirstTestCasesPage();

    setLoading('none');
  };

  const filterIssues = (issuesToFilter: Array<RepositoryIssue>) => {
    applyFilters();

    if (areFiltersEmpty()) {
      return issuesToFilter;
    }

    return filterOutIssues(
      issuesToFilter,
      {
        projectsForQuery: getProjectsForQuery(),
        statusesForQuery: getStatusesForQuery(),
        testTypesForQuery: getTestTypeForQuery(),
        textSearchFilter,
      },
      testCasesTestTypes
    );
  };

  const filterIssuesInFolder = (issuesToFilter: Array<RepositoryIssue>) => {
    const newIssues = filterIssues(issuesToFilter);

    setIssues(newIssues);
    setTotalIssues(newIssues.length);
  };

  const getTestCasesPage = async (
    shouldFetchFirst = false
  ): Promise<IssuesAndTotal> => {
    const projectsForQuery = getProjectsForQuery();

    const statusesForQuery = getStatusesForQuery();

    const testTypesForQuery = getTestTypeForQuery();

    const testCaseIdsMatchingFilters = await getTestCaseIdsMatchingFilters(
      testTypesForQuery
    );

    const filters = {
      text: textSearchFilter,
      projects: projectsForQuery.map((p) => p.key),
      statuses: statusesForQuery.map((s) => s.id),
      issueIds: testCaseIdsMatchingFilters,
      orderBy: 'updated DESC',
    };

    return await getNextTestCasesSearchPaginated(
      NUMBER_OF_TEST_CASES_PER_PAGE_LIMIT,
      filters,
      shouldFetchFirst
    );
  };

  const getFirstTestCasesPage = async () => {
    pageNumberRef.current = 0;
    resetPageableTokens();
    applyFilters();

    const newIssuesAndTotal = await getTestCasesPage(true);

    setIssues(newIssuesAndTotal.issues);
    setTotalIssues(newIssuesAndTotal.total);
    setHasMoreIssues(newIssuesAndTotal.total > newIssuesAndTotal.issues.length);
    setAllIssuesInFolder([]);
    pageNumberRef.current = 1;
    if (IS_ADVANCED) {
      addMissingTestTypes(newIssuesAndTotal.issues);
    }
  };

  const getNextTestCasesPage = async () => {
    const newIssuesAndTotal = await getTestCasesPage();

    const oldAndNewIssues = [...issues, ...newIssuesAndTotal.issues];

    setIssues(oldAndNewIssues);
    setTotalIssues(newIssuesAndTotal.total);
    setHasMoreIssues(newIssuesAndTotal.total > oldAndNewIssues.length);
    pageNumberRef.current += 1;
    if (IS_ADVANCED) {
      addMissingTestTypes(newIssuesAndTotal.issues);
    }
  };

  const searchIssues = async () => {
    setLoading('search');
    if (folder?.id) {
      filterIssuesInFolder(allIssuesInFolder);
    } else {
      await getFirstTestCasesPage();
    }
    setLoading('none');
  };

  const searchMoreIssues = async () => {
    if (!hasMoreIssues) {
      return;
    }

    setLoading('search-more');
    await getNextTestCasesPage();
    setLoading('none');
  };

  const deleteTestCasesFromFolder = (
    testCasesInFolder: Array<TestCaseInFolder>
  ) => {
    updateDeleteIssuesFromFolder(testCasesInFolder);
  };

  const createTestCases = async (project: Project) => {
    try {
      const filterOutNonTestCases: Filter<Issue> = (issue) => {
        return issue.fields.issuetype.name === QALITY_TEST_ISSUE_TYPE;
      };

      const newIssues = await DialogUtils.createTestCaseDialog(project);
      const newTestCases = newIssues.filter(filterOutNonTestCases);

      if (newTestCases.length === 0) {
        return;
      }

      setLoading('create-test-case');

      currentView === 'all-test-cases'
        ? createTestCasesInAllTestCases(newTestCases)
        : await createTestCasesInFolder(newTestCases);

      if (IS_ADVANCED) {
        addMissingTestTypes(newTestCases);
      }
    } catch {
      FlagService.createErrorFlag(
        'Error',
        'We experienced an error while creating a test case. Please try again.'
      );
    } finally {
      setLoading('none');
    }
  };

  const createTestCasesInAllTestCases = (newIssues: Array<Issue>) => {
    const newFilteredIssues = filterIssues(newIssues);

    updateIssuesInAllTestCases(newFilteredIssues);

    const newIssuesCount = newIssues.length;
    const newFilteredIssuesCount = newFilteredIssues.length;

    const areAllNewIssuesMatchingFilterCriteria =
      newIssuesCount - newFilteredIssuesCount === 0;

    if (!areAllNewIssuesMatchingFilterCriteria) {
      FlagService.createInfoFlag(
        'Success',
        getCreateTestCaseInAllTestCasesWithFiltersFlagMessage(newIssuesCount)
      );
    }
  };

  const createTestCasesInFolder = async (newIssues: Array<Issue>) => {
    try {
      const { data: newTestCasesInFolder } =
        await FolderService.addTestCasesToFolder(
          newIssues.map((newIssue) => newIssue.id),
          folder.id
        );

      addToFolder(folder.id, newTestCasesInFolder);

      const newIssuesInFolder = newIssues.map<RepositoryIssue>((newIssue) => {
        return {
          ...newIssue,
          testCaseInFolder: newTestCasesInFolder.find(
            (newTestCaseInFolder) =>
              newTestCaseInFolder.testCaseId.toString() ===
              newIssue.id.toString()
          ),
        };
      });

      const { newIssues: newFilteredIssues } =
        updateAddIssuesInFolder(newIssuesInFolder);

      const newIssuesInFolderCount = newIssuesInFolder.length;
      const areAllNewIssuesInFolderMatchingFilterCriteria =
        getAreAllNewIssuesInFolderMatchingFilterCriteria(
          newIssuesInFolder,
          newFilteredIssues
        );

      areAllNewIssuesInFolderMatchingFilterCriteria
        ? FlagService.createSuccessFlag(
            'Success',
            getCreateTestCaseInFolderFlagMessage(newIssuesInFolderCount)
          )
        : FlagService.createInfoFlag(
            'Success',
            getCreateTestCaseInFolderWithFiltersFlagMessage(
              newIssuesInFolderCount
            )
          );
    } catch {
      FlagService.createErrorFlag(
        'Error',
        'Sorry, we could not add test cases to folder. Please try again.'
      );
    }
  };

  const updateIssuesInAllTestCases = (newIssuesArg: Array<RepositoryIssue>) => {
    const newIssues = [...newIssuesArg.reverse(), ...issues];
    const newTotalIssues = totalIssues + newIssuesArg.length;

    setIssues(newIssues);
    setTotalIssues(newTotalIssues);

    return { newIssues, newTotalIssues };
  };

  const updateDeleteIssuesFromFolder = (
    testCasesInFolderToDelete: Array<TestCaseInFolder>
  ): void => {
    const newAllIssuesInFolder = allIssuesInFolder.filter(
      (issue) =>
        !testCasesInFolderToDelete.some(
          (testCaseInFolderToDelete) =>
            testCaseInFolderToDelete.id === issue.testCaseInFolder?.id
        )
    );
    updateIssuesInFolder(newAllIssuesInFolder);
  };

  const updateAddIssuesInFolder = (
    newIssuesInFolder: Array<RepositoryIssue>
  ): {
    newAllIssuesInFolder: RepositoryIssue[];
    newIssues: RepositoryIssue[];
    newTotalIssues: number;
  } => {
    const newAllIssuesInFolder = [...allIssuesInFolder, ...newIssuesInFolder];
    return updateIssuesInFolder(newAllIssuesInFolder);
  };

  const updateTestCasesInFolder = async (
    testCasesInFolder: Array<TestCaseInFolder>
  ) => {
    const newAllIssuesInFolder = await getSelectedFolderIssues(
      testCasesInFolder
    );
    updateIssuesInFolder(newAllIssuesInFolder);
  };

  const updateIssuesInFolder = (
    newAllIssuesInFolder: Array<RepositoryIssue>
  ): {
    newAllIssuesInFolder: RepositoryIssue[];
    newIssues: RepositoryIssue[];
    newTotalIssues: number;
  } => {
    setAllIssuesInFolder(newAllIssuesInFolder);

    const newIssues = filterIssues(newAllIssuesInFolder);
    setIssues(newIssues);

    const newTotalIssues = newIssues.length;
    setTotalIssues(newTotalIssues);

    return { newAllIssuesInFolder, newIssues, newTotalIssues };
  };

  const getEmpty = () => {
    const isLoading = loading !== 'none';
    if (isLoading) {
      return 'none';
    }

    const hasIssues = issues?.length > 0;
    if (hasIssues) {
      return 'none';
    }

    const isInAllTestCasesView = currentView === 'all-test-cases';
    if (isInAllTestCasesView) {
      return 'all-test-cases';
    }

    const isInFolderView = currentView === 'folder';
    if (isInFolderView) {
      return areFiltersEmpty()
        ? 'folder-without-filter'
        : 'folder-with-filters';
    }

    return 'none';
  };

  async function searchForTestCasesTestTypes(
    folderIssues: Array<RepositoryIssue>
  ): Promise<Array<IssueTestType>> {
    const testCasesIdsAndUndefined = folderIssues.map((issue) => +issue?.id);
    const testCasesIds = testCasesIdsAndUndefined.filter(
      (id) => Utils.isNilOrEmptyOrNaN(id) === false
    );
    if (testCasesIds.length === 0) {
      return [];
    }
    const uniqueTestCasesIds = Array.from(new Set(testCasesIds));
    const res = await TestCasesService.getIssueTestTypes(uniqueTestCasesIds);
    return res as IssueTestType[];
  }

  const getTestCaseIdsMatchingFilters = async (
    testTypesForQuery: TestType[]
  ): Promise<number[]> => {
    if (testTypesForQuery.length === 0) {
      return [];
    }
    return await TestCasesService.getTestCaseIdsMatchingFilters(
      testTypesForQuery.map((testType) => testType.id)
    );
  };

  return (
    <TestCaseRepositoryIssuesContext.Provider
      value={{
        issues,
        loading,
        empty: getEmpty(),
        hasMoreIssues,
        searchIssues,
        searchMoreIssues,
        totalIssues,
        createTestCases,
        deleteTestCasesFromFolder,
        initFolder,
        initSubfolder,
        selectionKey,
      }}
    >
      {children}
    </TestCaseRepositoryIssuesContext.Provider>
  );
}
